import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DocumentTemplateService as ComponentService } from '../document-template.service';
import { AuthService } from 'src/app/services/auth.service';
import { finalize, forkJoin, map, takeUntil, Subject, debounceTime, distinctUntilChanged, startWith } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import Swal from 'sweetalert2';
import {
    DirectieData,
    CompartimentData,
    UserData,
} from '../../document-types/document-types.interface';
import { DocumentTypesService } from '../../document-types/document-types.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';

export interface OrderRedaction {
    id: number;
    user_type: number;
    order: number;
    redaction_type: null | number | string;
    functie: string;
    template_id: number;
    created_at: string;
    updated_at: string;
    // Optional fields for cascading assignments
    directie_id?: number | null;
    compartiment_id?: number | null;
    user_id?: number | null;
    // Helper fields for display
    directie_name?: string;
    compartiment_name?: string;
    user_name?: string;
}

@Component({
    selector: 'app-document-template-redactions',
    templateUrl: './document-template-redactions.component.html',
    styleUrls: ['./document-template-redactions.component.scss'],
})
export class DocumentTemplateRedactionsComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;

    successTitle: string = environment.config.customNotifications.headers.success;
    successIcon: string = environment.config.customNotifications.icons.success;
    successType: string = environment.config.customNotifications.icons.success;

    // Store compartimente and users per directie/compartiment for better management
    compartimenteByDirectie: { [directieId: number]: CompartimentData[] } = {};
    usersByDirectieCompartiment: { [key: string]: UserData[] } = {};

    // Typeahead search controls (per row index)
    directiiSearchControls: { [index: number]: FormControl } = {};
    compartimenteSearchControls: { [index: number]: FormControl } = {};
    usersSearchControls: { [index: number]: FormControl } = {};

    // Filtered data for typeahead (per row index)
    filteredDirectiiByIndex: { [index: number]: DirectieData[] } = {};
    filteredCompartimenteByIndex: { [index: number]: CompartimentData[] } = {};
    filteredUsersByIndex: { [index: number]: UserData[] } = {};

    helper = helper;

    token!: string;
    isLoading = false;
    isDragging = false;

    form: FormGroup = new FormGroup({
        name: new FormControl(this.data.name, [Validators.required]),
        context: new FormControl(this.data.context, [Validators.required]),
        type_id: new FormControl(this.data.type_id, [Validators.required]),
    });

    isChecked: boolean = false;
    saveClicked: boolean = false;
    functiiSelectedOrder: OrderRedaction[] = [];
    functiiOperator: any = [];
    functiiLoaded: boolean = false;

    // Data sources for new dropdown fields
    directiiList: DirectieData[] = [];

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };

    manual_override: boolean = false;

    // Redaction types instead of signature types
    redactionTypes: any[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        private dialogRef: MatDialogRef<DocumentTemplateRedactionsComponent>,
        private componentService: ComponentService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private documentTypesService: DocumentTypesService,
    ) {
        console.log('data', data);
        this.processToken();

        if (typeof this.data.manual_override !== 'undefined') {
            this.manual_override = this.data.manual_override;
            console.log('this.manual_override', this.manual_override);
            console.log('this.data.id', this.data.id);
        }
    }

    ngOnInit(): void {
        this.loadRedactionTypes();
        this.loadDataTemplate();
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    loadRedactionTypes() {
        // Redaction types: 1 = Actualizare/Upload, 2 = Review/Aprobare
        this.redactionTypes = [
            { label: '--Alegeți--', value: '-1' },
            { label: 'Actualizare/Upload', value: 1 },
            { label: 'Review/Aprobare', value: 2 },
        ];
    }

    loadDataTemplate() {
        this.isLoading = true;

        const functiiOperator$ = this.componentService
            .getFunctiiUserActiveList()
            .pipe(takeUntil(this.unsubscribe$));

        const redactionsOperator$ = this.componentService
            .redactionsTemplateOperatorListIds(this.data.id, this.manual_override)
            .pipe(
                takeUntil(this.unsubscribe$),
                map((res: any) => res.data)
            );

        // Load directii for cascading dropdowns
        const directii$ = this.documentTypesService
            .getAvailableDirectii()
            .pipe(
                takeUntil(this.unsubscribe$),
                map((res: any) => res.data || [])
            );

        forkJoin({ functiiOperator$, redactionsOperator$, directii$ })
            .pipe(
                takeUntil(this.unsubscribe$),
                map(({ functiiOperator$, redactionsOperator$, directii$ }) => {
                    this.functiiSelectedOrder = redactionsOperator$.order_redactions || [];
                    this.functiiOperator = functiiOperator$.map((functieOp: any) => ({
                        ...functieOp,
                        checked: redactionsOperator$.ids.includes(functieOp.id),
                    }));
                    this.directiiList = directii$;

                    // Initialize typeahead controls for all existing rows after a short delay
                    setTimeout(() => {
                        this.functiiSelectedOrder.forEach((row, index) => {
                            this.setupTypeaheadForRow(index);

                            // Preload compartimente and users if directie is selected
                            if (row.directie_id) {
                                this.loadCompartimenteForDirectie(row.directie_id);

                                const compartimentId = row.compartiment_id ?? undefined;
                                this.loadUsersForSelection(row.directie_id, compartimentId);
                            }
                        });
                    }, 0);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe();
    }

    /**
     * Load compartimente for a specific directie
     */
    loadCompartimenteForDirectie(directieId: number): void {
        if (this.compartimenteByDirectie[directieId]) {
            return;
        }

        this.documentTypesService
            .getCompartimenteByDirectie(directieId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (res: any) => {
                    this.compartimenteByDirectie[directieId] = res.data || [];

                    // Update filtered lists for all rows that have this directie selected
                    this.functiiSelectedOrder.forEach((row, index) => {
                        if (row.directie_id === directieId) {
                            this.filteredCompartimenteByIndex[index] = [...this.compartimenteByDirectie[directieId]];
                        }
                    });
                },
                error: (error) => {
                    console.error('Error loading compartimente:', error);
                    this.compartimenteByDirectie[directieId] = [];
                }
            });
    }

    /**
     * Load users filtered by directie and optionally compartiment
     */
    loadUsersForSelection(directieId: number, compartimentId?: number): void {
        const cacheKey = compartimentId ? `${directieId}-${compartimentId}` : `${directieId}`;

        if (this.usersByDirectieCompartiment[cacheKey]) {
            return;
        }

        this.documentTypesService
            .getAvailableUsers(this.data.id, directieId, compartimentId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (res: any) => {
                    this.usersByDirectieCompartiment[cacheKey] = res.data || [];

                    // Update filtered lists for all rows that match this directie + compartiment combination
                    this.functiiSelectedOrder.forEach((row, index) => {
                        const rowCompartimentId = row.compartiment_id ?? undefined;
                        const matches = row.directie_id === directieId &&
                                      (compartimentId === rowCompartimentId ||
                                       (!compartimentId && !rowCompartimentId));

                        if (matches) {
                            this.filteredUsersByIndex[index] = [...this.usersByDirectieCompartiment[cacheKey]];
                        }
                    });
                },
                error: (error) => {
                    console.error('Error loading users:', error);
                    this.usersByDirectieCompartiment[cacheKey] = [];
                }
            });
    }

    /**
     * Get compartimente for a specific directie from cache
     */
    getCompartimenteForDirectie(directieId: number | null | undefined): CompartimentData[] {
        if (!directieId) return [];
        return this.compartimenteByDirectie[directieId] || [];
    }

    /**
     * Get users for a specific directie and optionally compartiment from cache
     */
    getUsersForSelection(directieId: number | null | undefined, compartimentId?: number | null): UserData[] {
        if (!directieId) return [];
        const cacheKey = compartimentId ? `${directieId}-${compartimentId}` : `${directieId}`;
        return this.usersByDirectieCompartiment[cacheKey] || [];
    }

    /**
     * Handle directie selection change - cascading effect
     */
    onDirectieChange(row: OrderRedaction, index: number): void {
        row.compartiment_id = null;
        row.user_id = null;

        if (row.directie_id) {
            this.loadCompartimenteForDirectie(row.directie_id);
            this.loadUsersForSelection(row.directie_id);
        }
    }

    /**
     * Handle compartiment selection change - cascading effect
     */
    onCompartimentChange(row: OrderRedaction, index: number): void {
        row.user_id = null;

        if (row.directie_id && row.compartiment_id) {
            const compartimentId = row.compartiment_id ?? undefined;
            this.loadUsersForSelection(row.directie_id, compartimentId);
        } else if (row.directie_id) {
            this.loadUsersForSelection(row.directie_id);
        }
    }

    /**
     * Handle user selection change
     */
    onUserChange(row: OrderRedaction, index: number): void {
        // Currently no action needed, but keeping for extensibility
    }

    /**
     * Setup typeahead search control for a specific row
     */
    setupTypeaheadForRow(index: number): void {
        if (!this.directiiSearchControls[index]) {
            this.directiiSearchControls[index] = new FormControl('');
            this.compartimenteSearchControls[index] = new FormControl('');
            this.usersSearchControls[index] = new FormControl('');

            const row = this.functiiSelectedOrder[index];

            // Initialize filtered lists with full data
            this.filteredDirectiiByIndex[index] = [...this.directiiList];

            // Initialize compartimente list if directie is already selected
            if (row?.directie_id) {
                const compartimente = this.getCompartimenteForDirectie(row.directie_id);
                this.filteredCompartimenteByIndex[index] = [...compartimente];

                const users = this.getUsersForSelection(row.directie_id, row.compartiment_id);
                this.filteredUsersByIndex[index] = [...users];
            } else {
                this.filteredCompartimenteByIndex[index] = [];
                this.filteredUsersByIndex[index] = [];
            }

            // Setup typeahead for Directii
            this.directiiSearchControls[index].valueChanges
                .pipe(
                    startWith(''),
                    debounceTime(200),
                    distinctUntilChanged(),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe(searchTerm => {
                    this.filteredDirectiiByIndex[index] = this.filterDirectii(searchTerm);
                });

            // Setup typeahead for Compartimente
            this.compartimenteSearchControls[index].valueChanges
                .pipe(
                    startWith(''),
                    debounceTime(200),
                    distinctUntilChanged(),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe(searchTerm => {
                    const currentRow = this.functiiSelectedOrder[index];
                    const availableCompartimente = this.getCompartimenteForDirectie(currentRow?.directie_id);
                    this.filteredCompartimenteByIndex[index] = this.filterCompartimente(searchTerm, availableCompartimente);
                });

            // Setup typeahead for Users
            this.usersSearchControls[index].valueChanges
                .pipe(
                    startWith(''),
                    debounceTime(200),
                    distinctUntilChanged(),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe(searchTerm => {
                    const currentRow = this.functiiSelectedOrder[index];
                    const availableUsers = this.getUsersForSelection(currentRow?.directie_id, currentRow?.compartiment_id);
                    this.filteredUsersByIndex[index] = this.filterUsers(searchTerm, availableUsers);
                });
        }
    }

    /**
     * Normalize string for accent-insensitive search
     */
    normalizeString(value: string): string {
        if (!value) return '';
        return value
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
    }

    /**
     * Filter directii based on search term
     */
    filterDirectii(searchTerm: string): DirectieData[] {
        if (!searchTerm || searchTerm.trim() === '') {
            return [...this.directiiList];
        }
        const normalized = this.normalizeString(searchTerm);
        return this.directiiList.filter(directie =>
            this.normalizeString(directie.name).includes(normalized)
        );
    }

    /**
     * Filter compartimente based on search term
     */
    filterCompartimente(searchTerm: string, availableCompartimente: CompartimentData[]): CompartimentData[] {
        if (!searchTerm || searchTerm.trim() === '') {
            return [...availableCompartimente];
        }
        const normalized = this.normalizeString(searchTerm);
        return availableCompartimente.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalized)
        );
    }

    /**
     * Filter users based on search term
     */
    filterUsers(searchTerm: string, availableUsers: UserData[]): UserData[] {
        if (!searchTerm || searchTerm.trim() === '') {
            return [...availableUsers];
        }
        const normalized = this.normalizeString(searchTerm);
        return availableUsers.filter(user => {
            const name = user.name || user.full_name || '';
            return this.normalizeString(name).includes(normalized);
        });
    }

    /**
     * Called when directie dropdown opens
     */
    onDirectieDropdownOpened(index: number): void {
        this.setupTypeaheadForRow(index);
        this.directiiSearchControls[index]?.setValue('', { emitEvent: true });
    }

    /**
     * Called when compartiment dropdown opens
     */
    onCompartimentDropdownOpened(index: number): void {
        this.setupTypeaheadForRow(index);
        const row = this.functiiSelectedOrder[index];

        if (row?.directie_id) {
            this.loadCompartimenteForDirectie(row.directie_id);

            const availableCompartimente = this.getCompartimenteForDirectie(row.directie_id);
            this.filteredCompartimenteByIndex[index] = [...availableCompartimente];
        }

        this.compartimenteSearchControls[index]?.setValue('', { emitEvent: true });
    }

    /**
     * Called when user dropdown opens
     */
    onUserDropdownOpened(index: number): void {
        this.setupTypeaheadForRow(index);
        const row = this.functiiSelectedOrder[index];

        if (row?.directie_id) {
            const compartimentId = row.compartiment_id ?? undefined;
            this.loadUsersForSelection(row.directie_id, compartimentId);

            const availableUsers = this.getUsersForSelection(row.directie_id, row.compartiment_id);
            this.filteredUsersByIndex[index] = [...availableUsers];
        }

        this.usersSearchControls[index]?.setValue('', { emitEvent: true });
    }

    /**
     * Called when dropdown closes - clear search input
     */
    onDropdownClosed(index: number, type: 'directie' | 'compartiment' | 'user'): void {
        switch (type) {
            case 'directie':
                this.directiiSearchControls[index]?.setValue('', { emitEvent: false });
                break;
            case 'compartiment':
                this.compartimenteSearchControls[index]?.setValue('', { emitEvent: false });
                break;
            case 'user':
                this.usersSearchControls[index]?.setValue('', { emitEvent: false });
                break;
        }
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    save() {
        this.dialogRef?.close({
            success: true,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.componentService.setToken(token);
        });
    }

    updateRedactionsTemplateDocument(event: any) {
        event.stopPropagation();

        this.isLoading = true;
        this.componentService
            .updateRedactionsTemplateOperatorIds(this.data.id, {
                user_type_ids: this.functiiOperator
                    .filter((fo: any) => fo.checked)
                    .map((fo: any) => fo.id),
            }, this.manual_override)
            .subscribe({
                next: () => {
                    this.loadDataTemplate();
                    this.saveClicked = true;
                },
                error: (err: any) => {
                    this.isLoading = false;
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    drop(event: CdkDragDrop<OrderRedaction[]>): void {
        moveItemInArray(
            this.functiiSelectedOrder,
            event.previousIndex,
            event.currentIndex
        );

        // Update the order property of the reordered elements
        this.functiiSelectedOrder.forEach((item, index) => (item.order = index));
    }

    saveOrder(event: any) {
        event.stopPropagation();

        // Validate required fields before saving
        const validationErrors = this.validateRedactions();
        if (validationErrors.length > 0) {
            this.notificationService.warningSwal(
                this.errorTitle,
                validationErrors.join('<br>'),
                this.errorIcon
            );
            return;
        }

        this.isLoading = true;
        this.componentService
            .updateRedactions(this.functiiSelectedOrder, this.manual_override)
            .subscribe({
                next: () => {
                    console.log('saveOrder');
                    this.notificationService.successSwal(
                        this.successTitle,
                        'Configurația redactărilor a fost salvată cu succes!'
                    );
                    this.loadDataTemplate();
                },
                error: (err: any) => {
                    this.isLoading = false;
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    /**
     * Validate that all redactions have required fields filled
     */
    validateRedactions(): string[] {
        const errors: string[] = [];

        this.functiiSelectedOrder.forEach((redaction, index) => {
            const rowNumber = index + 1;

            // Validate functie (user_type) - required
            if (!redaction.user_type) {
                errors.push(`Rândul ${rowNumber}: Funcția este obligatorie.`);
            }

            // Validate redaction_type - required and not default value
            if (!redaction.redaction_type || redaction.redaction_type === '-1' || redaction.redaction_type === -1) {
                errors.push(`Rândul ${rowNumber} (${redaction.functie}): Tipul de redactare este obligatoriu.`);
            }

            // Validate directie_id - required
            if (!redaction.directie_id) {
                errors.push(`Rândul ${rowNumber} (${redaction.functie}): Direcția este obligatorie.`);
            }

            // Validate compartiment_id - required
            if (!redaction.compartiment_id) {
                errors.push(`Rândul ${rowNumber} (${redaction.functie}): Compartimentul este obligatoriu.`);
            }
        });

        return errors;
    }

    // Helper methods using the project's notification patterns
    private showSuccessMessage(message: string): void {
        Swal.fire({
            title: 'Succes',
            text: message,
            icon: 'success',
            confirmButtonText: this.helper.dialogConfig.buttonLabels.ok,
        });
    }

    private showErrorMessage(message: string): void {
        this.notificationService.warningSwal(
            this.errorTitle,
            message,
            this.errorIcon
        );
    }
}
